import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ShoppingCart, ChevronDown, ArrowRight, User } from 'lucide-react';
import { Badge } from './ui/badge';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';

const WA = 'https://wa.me/917011370322';
const wa = (text) => `${WA}?text=${encodeURIComponent(text)}`;

const FAMILY_ITEMS = [
  {
    id: 'smartbot',
    name: 'SMARTBOT',
    sub: 'Floor Cleaning Robots',
    tagline: 'Vacuum · Mop · AI Navigation · Smart Mapping',
    path: '/floor-cleaning-robots',
    tint: 'text-emerald-700',
    image: 'https://customer-assets.emergentagent.com/job_80fffe0f-7ad4-44ce-aa36-08a4bccc4edb/artifacts/4ik16avb_PHOTO-2026-05-30-18-18-13.jpg',
  },
  {
    id: 'shinebot',
    name: 'SHINEBOT',
    sub: 'Window Cleaning Robots',
    tagline: 'Glass Cleaning · Smart Navigation · Automated Cleaning',
    path: '/window-cleaning-robots',
    tint: 'text-cyan-700',
    image: 'https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/6j2kav5l_Window%20robo.jpeg',
  },
  {
    id: 'poolbot',
    name: 'POOLBOT',
    sub: 'Pool Cleaning Robots',
    tagline: 'Smart Navigation · Wall Climbing · Deep Filtration',
    path: '/pool-cleaning-robots',
    tint: 'text-blue-700',
    image: 'https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/y34dz1wb_PHOTO-2025-12-24-16-57-25.jpg',
  },
  {
    id: 'lawnbot',
    name: 'LAWNBOT',
    sub: 'Robotic Lawn Mowers',
    tagline: 'RTK · AI Vision · Precision Cutting',
    path: '/robotic-lawn-mowers',
    tint: 'text-green-700',
    image: 'https://customer-assets.emergentagent.com/job_smart-living-18/artifacts/h221a4vl_4885c0df-e93f-4fc7-b5f9-c51e42ee3ac0-md.jpeg',
  },
];

const PRODUCT_LINKS = [
  { label: 'LumineX 300', href: '/products/luminex-300' },
  { label: 'HygienX 300', href: '/products/hygienx-300' },
  { label: 'Otto A1 Pro', href: '/products/otto-a1-pro' },
  { label: 'Escargot Commercial', href: '/products/escargot-commercial' },
  { label: 'Robo Dressel', href: '/products/robo-dressel' },
  { label: 'Robo Titan', href: '/products/robo-titan' },
  { label: 'ShineBot C2', href: '/products/shinebot-c2' },
  { label: 'Greenova 5.0', href: '/products/greenova-5' },
  { label: 'HL-AP25 Plus Air Purifier', href: '/products/hl-ap25-plus' },
  { label: 'Airovac Plus', href: '/products/airovac-plus' },
  { label: 'Accessories', href: '/accessories' },
];

const MENUS = [
  {
    id: 'applications',
    label: 'Applications',
    items: [
      { label: 'Homes', href: '/#robot-families' },
      { label: 'Hotels & Resorts', href: '/solutions/hotels' },
      { label: 'Offices', href: '/solutions/corporate-offices' },
      { label: 'Commercial Facilities', href: '/solutions/facility-management' },
      { label: 'Pools', href: '/pool-cleaning-robots' },
      { label: 'Lawns', href: '/robotic-lawn-mowers' },
    ],
  },
  {
    id: 'support',
    label: 'Support',
    items: [
      { label: 'Warranty', href: '/warranty-policy' },
      { label: 'Installation & Delivery', href: '/shipping-policy' },
      { label: 'Contact Support', href: '/support' },
    ],
  },
  {
    id: 'company',
    label: 'Company',
    items: [
      { label: 'About Harpalyk', href: '/about-us' },
      { label: 'Innovation', href: '/#innovation' },
      { label: 'Why Harpalyk', href: '/#why-harpalyk' },
      { label: 'Dealer Registration', href: '/dealer' },
      { label: 'Blog', href: '/blog' },
      { label: 'Contact', href: '/#contact' },
    ],
  },
];

const Header = ({ scrolled }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { getCartCount, setIsCartOpen } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const isHomePage = location.pathname === '/';

  const closeAll = () => {
    setOpenMenu(null);
    setMobileMenuOpen(false);
  };

  const goTo = (href, external) => {
    closeAll();
    if (external) {
      window.open(href, '_blank', 'noopener');
      return;
    }
    if (href.startsWith('/#') && isHomePage) {
      document.getElementById(href.slice(2))?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    window.location.href = href;
  };

  const linkClass = 'text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors duration-300';

  return (
    <header
      data-testid="main-header"
      className={`fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-black/5 transition-shadow duration-500 ${
        scrolled ? 'shadow-sm' : ''
      }`}
      onMouseLeave={() => setOpenMenu(null)}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-16 lg:h-[72px]">
          <Link
            to="/"
            data-testid="logo-button"
            className="font-heading text-2xl font-bold tracking-tight text-gray-900 hover:opacity-80 transition-opacity duration-300"
            onClick={(e) => {
              closeAll();
              if (isHomePage) {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }
            }}
          >
            Harpalyk
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-7">
            <button
              data-testid="nav-robots"
              onMouseEnter={() => setOpenMenu('robots')}
              onClick={() => setOpenMenu(openMenu === 'robots' ? null : 'robots')}
              className={`${linkClass} flex items-center gap-1`}
            >
              Robots
              <ChevronDown size={14} className={`transition-transform duration-300 ${openMenu === 'robots' ? 'rotate-180' : ''}`} />
            </button>

            {MENUS.map((menu) => (
              <button
                key={menu.id}
                data-testid={`nav-${menu.id}`}
                onMouseEnter={() => setOpenMenu(menu.id)}
                onClick={() => setOpenMenu(openMenu === menu.id ? null : menu.id)}
                className={`${linkClass} flex items-center gap-1`}
              >
                {menu.label}
                <ChevronDown size={14} className={`transition-transform duration-300 ${openMenu === menu.id ? 'rotate-180' : ''}`} />
              </button>
            ))}

            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-button-desktop"
              className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
              onMouseEnter={() => setOpenMenu(null)}
            >
              <ShoppingCart size={19} className="text-gray-700" />
              {getCartCount() > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-500 text-white text-xs">
                  {getCartCount()}
                </Badge>
              )}
            </button>

            <Link
              to={user ? '/account' : '/login'}
              data-testid="account-button-desktop"
              className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
              title={user ? 'My Account' : 'Sign In'}
            >
              <User size={19} className={user ? 'text-emerald-600' : 'text-gray-700'} />
            </Link>
          </nav>

          {/* Mobile buttons */}
          <div className="lg:hidden flex items-center gap-1">
            <Link
              to={user ? '/account' : '/login'}
              data-testid="account-button-mobile"
              className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
            >
              <User size={20} className={user ? 'text-emerald-600' : 'text-gray-900'} />
            </Link>
            <button
              onClick={() => setIsCartOpen(true)}
              data-testid="cart-button-mobile"
              className="relative p-2 rounded-full hover:bg-gray-100 transition-colors duration-300"
            >
              <ShoppingCart size={20} className="text-gray-900" />
              {getCartCount() > 0 && (
                <Badge className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 bg-emerald-500 text-white text-xs">
                  {getCartCount()}
                </Badge>
              )}
            </button>
            <button
              className="p-2 text-gray-900"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* ROBOTS mega menu */}
      {openMenu === 'robots' && (
        <div
          data-testid="robots-mega-menu"
          className="hidden lg:block absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-black/5 shadow-xl animate-in fade-in slide-in-from-top-2 duration-300"
          onMouseEnter={() => setOpenMenu('robots')}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-8">
            <div className="grid grid-cols-4 gap-6">
              {FAMILY_ITEMS.map((fam) => (
                <button
                  key={fam.id}
                  data-testid={`mega-menu-${fam.id}`}
                  onClick={() => goTo(fam.path)}
                  className="group rounded-2xl border border-transparent hover:border-black/5 hover:bg-[#F8F9FA] p-4 transition-colors duration-300 text-left"
                >
                  <div className="h-32 rounded-xl overflow-hidden bg-[#F8F9FA] mb-4">
                    <img src={fam.image} alt={`Harpalyk ${fam.name} — ${fam.sub}`} loading="lazy" className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <h4 className={`font-heading text-sm font-bold tracking-wide ${fam.tint}`}>{fam.name}</h4>
                  <p className="text-xs font-medium text-gray-700 mb-1">{fam.sub}</p>
                  <p className="text-xs text-gray-500 leading-relaxed mb-2">{fam.tagline}</p>
                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600">
                    Explore {fam.name} <ArrowRight size={12} className="group-hover:translate-x-0.5 transition-transform duration-300" />
                  </span>
                </button>
              ))}
            </div>
            <div className="mt-6 pt-4 border-t border-black/5">
              <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase mb-2">Our Products</p>
              <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                {PRODUCT_LINKS.map((p) => (
                  <button
                    key={p.label}
                    data-testid={`mega-product-${p.href.split('/').pop()}`}
                    onClick={() => goTo(p.href)}
                    className="text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors duration-200"
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 text-right">
              <button
                onClick={() => goTo('/#products')}
                data-testid="mega-menu-view-all"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-900 hover:text-emerald-700 transition-colors duration-300"
              >
                View All Products <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Simple dropdown menus */}
      {MENUS.map(
        (menu) =>
          openMenu === menu.id && (
            <div
              key={menu.id}
              data-testid={`${menu.id}-menu`}
              className="hidden lg:block absolute top-full left-0 right-0 bg-white/95 backdrop-blur-xl border-b border-black/5 shadow-xl animate-in fade-in slide-in-from-top-2 duration-300"
              onMouseEnter={() => setOpenMenu(menu.id)}
            >
              <div className="max-w-7xl mx-auto px-6 lg:px-12 py-6">
                <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase mb-3">{menu.label}</p>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 max-w-2xl">
                  {menu.items.map((item) => (
                    <button
                      key={item.label}
                      data-testid={`menu-${menu.id}-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                      onClick={() => goTo(item.href, item.external)}
                      className="py-2 text-left text-sm font-medium text-gray-700 hover:text-emerald-700 transition-colors duration-200"
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-xl border-t border-black/5 animate-in slide-in-from-top duration-300 max-h-[75vh] overflow-y-auto">
          <nav className="flex flex-col px-6 py-4">
            <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase py-2">Robots</p>
            {FAMILY_ITEMS.map((fam) => (
              <button
                key={fam.id}
                data-testid={`mobile-nav-${fam.id}`}
                onClick={() => goTo(fam.path)}
                className="py-2.5 pl-3 text-left border-l-2 border-black/5 hover:border-emerald-500 transition-colors duration-200"
              >
                <span className={`block text-sm font-bold tracking-wide ${fam.tint}`}>{fam.name}</span>
                <span className="block text-xs text-gray-500">{fam.sub}</span>
              </button>
            ))}
            <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase pt-4 pb-1">Our Products</p>
            {PRODUCT_LINKS.map((p) => (
              <button
                key={p.label}
                data-testid={`mobile-product-${p.href.split('/').pop()}`}
                onClick={() => goTo(p.href)}
                className="block w-full py-2 pl-3 text-left text-sm font-medium text-gray-800 border-l-2 border-black/5 hover:border-emerald-500 hover:text-emerald-700 transition-colors duration-200"
              >
                {p.label}
              </button>
            ))}
            {MENUS.map((menu) => (
              <div key={menu.id}>
                <p className="text-xs font-semibold tracking-[0.2em] text-gray-400 uppercase pt-4 pb-1">{menu.label}</p>
                {menu.items.map((item) => (
                  <button
                    key={item.label}
                    data-testid={`mobile-${menu.id}-${item.label.toLowerCase().replace(/[\s&]+/g, '-')}`}
                    onClick={() => goTo(item.href, item.external)}
                    className="block w-full py-2 pl-3 text-left text-sm font-medium text-gray-800 border-l-2 border-black/5 hover:border-emerald-500 hover:text-emerald-700 transition-colors duration-200"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
